import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon as ExclamationCircleSolidIcon } from '@heroicons/react/24/solid';
import { cn } from '@restworld/utils-common';

const alertVariants = cva('flex flex-row items-center gap-2 p-3 rounded-lg border self-stretch', {
  variants: {
    variant: {
      success: 'border-green-400 bg-green-50 rounded-md items-center',
      error: 'border-red-600 bg-red-50',
      info: 'bg-yellow-100 border-l-4 border-amber-500',
      warning: 'border-orange-700 bg-orange-50 border rounded-lg',
      restworld: 'border-purple-700 bg-purple-50',
    },
  },
  defaultVariants: {
    variant: 'error',
  },
});

const alertTextVariants = cva('text-sm leading-[21px] lg:text-base lg:leading-[21px]', {
  variants: {
    variant: {
      success: 'text-green-700',
      error: 'text-red-600',
      info: 'text-yellow-800',
      warning: 'text-orange-700',
      restworld: 'text-purple-700 font-medium',
    },
  },
  defaultVariants: {
    variant: 'error',
  },
});

const defaultIcons: Record<string, React.ReactNode> = {
  success: <CheckCircleIcon className="text-green-700 min-w-[18px] min-h-[18px] max-w-[18px] max-h-[18px]" />,
  error: (
    <ExclamationCircleIcon className="relative text-red-600 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px]" />
  ),
  info: (
    <ExclamationCircleSolidIcon className="text-yellow-700 rotate-180 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px]" />
  ),
  warning: <InformationCircleIcon className="fill-orange-700 min-w-[16px] min-h-[16px] max-w-[16px] max-h-[16px]" />,
  restworld: (
    <InformationCircleIcon className="relative text-purple-600 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px]" />
  ),
};

export type AlertProps = VariantProps<typeof alertVariants> & {
  text: string | React.ReactNode;
  className?: string;
  textClassName?: string;
  icon?: React.ReactNode;
};

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, textClassName, icon, text, variant = 'error', ...props }, ref) => {
    return (
      <div ref={ref} className={cn(alertVariants({ variant }), className)} {...props}>
        {icon ? icon : variant ? defaultIcons[variant] : null}
        <p className={cn(alertTextVariants({ variant }), textClassName)}>{text}</p>
      </div>
    );
  }
);
Alert.displayName = 'Alert';

export { alertVariants, alertTextVariants };
